import { resolveJobPresentation } from "./job-presentation";
import type {
  ConversationMessageShowRefs,
  SessionHeapSnapshot,
  SessionTranscriptEntry,
} from "./types";

const EMPTY_HEAP: SessionHeapSnapshot = {
  entriesByPath: {},
  listsByName: {},
  variablesByName: {},
  updatedAt: 0,
};

type TranscriptJobRecord = {
  jobId?: unknown;
  status?: unknown;
  submittedAt?: unknown;
  startedAt?: unknown;
  finishedAt?: unknown;
  source?: unknown;
  result?: unknown;
  error?: unknown;
  prompts?: unknown;
  agentMessages?: unknown;
};

type TranscriptConversationRecord = {
  id?: unknown;
  role?: unknown;
  content?: unknown;
  reply?: unknown;
  message?: unknown;
  text?: unknown;
  show?: unknown;
  ts?: unknown;
  timestamp?: unknown;
  jobId?: unknown;
  promptId?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function trimString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeShowRefs(
  value: unknown,
): ConversationMessageShowRefs | undefined {
  const record = asRecord(value);
  if (!record) return undefined;

  const normalizeRefs = (input: unknown): string[] | undefined => {
    if (!Array.isArray(input)) return undefined;
    const refs = Array.from(
      new Set(
        input
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    );
    return refs.length > 0 ? refs : undefined;
  };

  const show = {
    entryPaths: normalizeRefs(record.entryPaths),
    listNames: normalizeRefs(record.listNames),
    variableNames: normalizeRefs(record.variableNames),
    fileIds: normalizeRefs(record.fileIds),
  };

  return show.entryPaths || show.listNames || show.variableNames || show.fileIds
    ? show
    : undefined;
}

function stringifyTranscriptValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    return value.trim() || fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Confirmed" : "Canceled";
  }

  if (value === undefined) {
    return fallback;
  }

  try {
    const json = JSON.stringify(value, null, 2);
    if (!json || json === "undefined") return fallback;
    return json.length > 2_000 ? `${json.slice(0, 2_000)}...` : json;
  } catch {
    return String(value);
  }
}

function buildArtifactHistory(
  show?: ConversationMessageShowRefs,
): string | undefined {
  if (!show) return undefined;
  return `[Agent message]\n${stringifyTranscriptValue({ show }, "")}`;
}

function normalizeConversationMessage(
  raw: unknown,
): SessionTranscriptEntry | null {
  const record = asRecord(raw) as TranscriptConversationRecord | null;
  if (!record) return null;

  const role =
    record.role === "user"
      ? "user"
      : record.role === "assistant"
        ? "assistant"
        : null;
  if (!role) return null;

  const content = trimString(
    record.content ?? record.reply ?? record.message ?? record.text,
  );
  const show = normalizeShowRefs(record.show);
  const id = asString(record.id) || crypto.randomUUID();
  const timestamp = asNumber(record.timestamp) || asNumber(record.ts) || 0;

  if (!content && !show) return null;

  return {
    id,
    role,
    content,
    timestamp,
    jobId: asString(record.jobId),
    promptId: asString(record.promptId),
    show,
    historyContent:
      role === "assistant"
        ? content
          ? `[Assistant reply]\n${content}`
          : buildArtifactHistory(show)
        : undefined,
    source: "conversation",
  };
}

function normalizePromptEntries(
  jobId: string,
  rawPrompts: unknown,
  conversationPromptIds: Set<string>,
): SessionTranscriptEntry[] {
  const promptsById = asRecord(rawPrompts) || {};
  return Object.values(promptsById)
    .map((value) => asRecord(value))
    .filter((value): value is Record<string, unknown> => Boolean(value))
    .sort(
      (left, right) =>
        (asNumber(left.openedAt) || asNumber(left.answeredAt) || 0) -
        (asNumber(right.openedAt) || asNumber(right.answeredAt) || 0),
    )
    .flatMap((prompt) => {
      const promptId = asString(prompt.promptId);
      if (!promptId || conversationPromptIds.has(promptId)) return [];

      const title = trimString(prompt.title);
      const message = trimString(prompt.message);
      const assistantContent = message || title || "Input required";
      const openedAt = asNumber(prompt.openedAt) || 0;
      const answeredAt = asNumber(prompt.answeredAt) || openedAt;
      const entries: SessionTranscriptEntry[] = [
        {
          id: `prompt:${promptId}:assistant`,
          role: "assistant",
          content: assistantContent,
          timestamp: openedAt,
          jobId,
          promptId,
          historyContent: `[Assistant reply]\n${assistantContent}`,
          source: "job_prompt",
        },
      ];

      if (Object.prototype.hasOwnProperty.call(prompt, "answer")) {
        entries.push({
          id: `prompt:${promptId}:user`,
          role: "user",
          content: stringifyTranscriptValue(prompt.answer, ""),
          timestamp: answeredAt,
          jobId,
          promptId,
          source: "job_prompt",
        });
      }

      return entries;
    });
}

function normalizeAgentMessageEntries(
  jobId: string,
  rawMessages: unknown,
): SessionTranscriptEntry[] {
  return asArray(rawMessages)
    .map((value) => asRecord(value))
    .filter((value): value is Record<string, unknown> => Boolean(value))
    .sort(
      (left, right) =>
        (asNumber(left.timestamp) || asNumber(left.ts) || 0) -
        (asNumber(right.timestamp) || asNumber(right.ts) || 0),
    )
    .flatMap((message) => {
      const messageId =
        asString(message.messageId) ||
        asString(message.id) ||
        crypto.randomUUID();
      const timestamp =
        asNumber(message.timestamp) || asNumber(message.ts) || 0;
      const reply = trimString(
        message.reply ?? message.message ?? message.text ?? message.content,
      );
      const show = normalizeShowRefs(message.show);
      const entries: SessionTranscriptEntry[] = [];

      if (reply) {
        entries.push({
          id: `agent:${messageId}:text`,
          role: "assistant",
          content: reply,
          timestamp,
          jobId,
          historyContent: `[Assistant reply]\n${reply}`,
          source: "job_agent_message",
        });
      }

      if (show) {
        entries.push({
          id: `agent:${messageId}:artifacts`,
          role: "assistant",
          content: "",
          timestamp,
          jobId,
          show,
          historyContent: buildArtifactHistory(show),
          source: "job_agent_message",
        });
      }

      return entries;
    });
}

function buildJobFallbackEntries(
  jobId: string,
  job: Record<string, unknown>,
  sessionHeap: SessionHeapSnapshot,
): SessionTranscriptEntry[] {
  const timestamp =
    asNumber(job.finishedAt) ||
    asNumber(job.startedAt) ||
    asNumber(job.submittedAt) ||
    0;
  const resultPreview = stringifyTranscriptValue(
    job.result,
    "No job result recorded.",
  );
  const presentation = resolveJobPresentation({
    jobId,
    result: job.result,
    stdout: [],
    sessionHeap,
  });
  const entries: SessionTranscriptEntry[] = [];
  const responseText = presentation.responseText || "";

  if (responseText) {
    entries.push({
      id: `job:${jobId}:result-text`,
      role: "assistant",
      content: responseText,
      timestamp,
      jobId,
      historyContent: `[Assistant reply]\n${responseText}`,
      source: "job_result",
    });
  }

  const show: ConversationMessageShowRefs = {
    entryPaths: presentation.entries.map((entry) => entry.path),
    listNames: presentation.lists.map((list) => list.name),
  };
  if (
    (show.entryPaths && show.entryPaths.length > 0) ||
    (show.listNames && show.listNames.length > 0)
  ) {
    entries.push({
      id: `job:${jobId}:result-artifacts`,
      role: "assistant",
      content: "",
      timestamp,
      jobId,
      show,
      historyContent: buildArtifactHistory(show),
      source: "job_result",
    });
  }

  if (entries.length === 0 && trimString(job.error)) {
    entries.push({
      id: `job:${jobId}:result-error`,
      role: "assistant",
      content: trimString(job.error),
      timestamp,
      jobId,
      historyContent: `[Assistant reply]\n${trimString(job.error)}`,
      source: "job_result",
    });
  }

  if (
    entries.length === 0 &&
    resultPreview &&
    resultPreview !== "No job result recorded."
  ) {
    entries.push({
      id: `job:${jobId}:result-preview`,
      role: "assistant",
      content: resultPreview,
      timestamp,
      jobId,
      historyContent: `[Assistant reply]\n${resultPreview}`,
      source: "job_result",
    });
  }

  return entries;
}

function buildJobCodeEntry(
  jobId: string,
  job: Record<string, unknown>,
): SessionTranscriptEntry | null {
  const code = trimString(job.source);
  if (!code) return null;

  const jobStatus = asString(job.status);
  const error =
    jobStatus === "failed" ||
    jobStatus === "canceled" ||
    jobStatus === "timeout"
      ? trimString(job.error) || `Job ${jobStatus}`
      : undefined;

  return {
    id: `job:${jobId}:code`,
    role: "assistant",
    content: "",
    timestamp:
      asNumber(job.submittedAt) ||
      asNumber(job.startedAt) ||
      asNumber(job.finishedAt) ||
      0,
    jobId,
    code,
    jobStatus,
    jobResultPreview: stringifyTranscriptValue(
      job.result,
      "No job result recorded.",
    ),
    error,
    source: "job_code",
  };
}

export function buildSessionTranscript(input: {
  liveDoc?: Record<string, unknown> | null;
  sessionHeap?: SessionHeapSnapshot;
}): SessionTranscriptEntry[] {
  const liveDoc = input.liveDoc || null;
  const sessionHeap = input.sessionHeap || EMPTY_HEAP;
  const transcript: SessionTranscriptEntry[] = [];

  const conversationMessages = asArray(
    asRecord(liveDoc?.conversation)?.messages,
  )
    .map((message) => normalizeConversationMessage(message))
    .filter((message): message is SessionTranscriptEntry => Boolean(message));
  const conversationPromptIds = new Set(
    conversationMessages
      .map((message) => message.promptId)
      .filter((promptId): promptId is string => Boolean(promptId)),
  );
  const assistantConversationJobIds = new Set(
    conversationMessages
      .filter(
        (message) => message.role === "assistant" && Boolean(message.jobId),
      )
      .map((message) => message.jobId as string),
  );

  transcript.push(...conversationMessages);

  const jobsById = asRecord(asRecord(liveDoc?.jobs)?.byId) || {};
  const jobs = Object.values(jobsById)
    .map((value) => asRecord(value) as TranscriptJobRecord | null)
    .filter((value): value is TranscriptJobRecord => Boolean(value))
    .sort(
      (left, right) =>
        (asNumber(left.submittedAt) ||
          asNumber(left.startedAt) ||
          asNumber(left.finishedAt) ||
          0) -
        (asNumber(right.submittedAt) ||
          asNumber(right.startedAt) ||
          asNumber(right.finishedAt) ||
          0),
    );

  for (const job of jobs) {
    const jobId = asString(job.jobId);
    if (!jobId) continue;

    const codeEntry = buildJobCodeEntry(jobId, job as Record<string, unknown>);
    if (codeEntry) {
      transcript.push(codeEntry);
    }

    transcript.push(
      ...normalizePromptEntries(jobId, job.prompts, conversationPromptIds),
    );

    if (!assistantConversationJobIds.has(jobId)) {
      const agentEntries = normalizeAgentMessageEntries(
        jobId,
        job.agentMessages,
      );
      if (agentEntries.length > 0) {
        transcript.push(...agentEntries);
      } else {
        transcript.push(
          ...buildJobFallbackEntries(
            jobId,
            job as Record<string, unknown>,
            sessionHeap,
          ),
        );
      }
    }
  }

  return transcript.sort((left, right) => {
    if (left.timestamp !== right.timestamp) {
      return left.timestamp - right.timestamp;
    }
    return left.id.localeCompare(right.id);
  });
}
